import { useTranslate } from '@tolgee/react';
import { message } from 'antd';
import { confirmReturn, disputeReturn, returnReviewResolve } from 'services/api/return';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import MercadoOffer from 'components/Refund/MercadoOffer';
import StatusButton from 'components/StatusButton';
import { formatNumber } from 'utils/numberFormat';
import { ReturnReviewReject } from './ReturnReviewReject';
import { ReturnActionMercadoProps } from './types';

const ReturnRefund = ({
  externalStoreId,
  externalTradeId,
  returnSn,
  platform,
  refreshList,
}: ReturnActionMercadoProps) => {
  const { t } = useTranslate();

  return (
    <StatusButton
      size="small"
      popConfirmProps={{
        title: t('scene.orderWorkspace.returnRefundTab.dialog.confirmAcceptReturnRefund'),
      }}
      onClick={() =>
        confirmReturn({
          externalStoreId,
          externalTradeId,
          returnSn,
          externalType: platform,
          operate: 'CONFIRM_RETURN_REFUND',
        })
          .then(() => {
            refreshList?.();
            message.success(t('common.feedback.saveSuccess'));
          })
          .catch((err) => {
            message.error(getApiErrorCodeMsg(err.response));
          })
      }
    >
      {t('scene.orderWorkspace.returnRefundTab.list.action.approveReturnRefund')}
    </StatusButton>
  );
};

const OnlyRefund = ({
  externalStoreId,
  externalTradeId,
  returnSn,
  platform,
  unit,
  refundAmount,
  refreshList,
}: ReturnActionMercadoProps) => {
  const { t } = useTranslate();

  return (
    <StatusButton
      size="small"
      popConfirmProps={{
        title: t('scene.orderWorkspace.returnRefundTab.detail.hint.fullRefund', {
          money: `${unit}${formatNumber(refundAmount, '0,0.00')}`,
        }),
      }}
      onClick={() =>
        confirmReturn({
          externalStoreId,
          externalTradeId,
          returnSn,
          externalType: platform,
          operate: 'CONFIRM_ONLY_REFUND',
        })
          .then(() => {
            refreshList?.();
            message.success(t('common.feedback.saveSuccess'));
          })
          .catch((err) => {
            message.error(getApiErrorCodeMsg(err.response));
          })
      }
    >
      {t('scene.orderWorkspace.returnRefundTab.list.action.approveRefundOnly')}
    </StatusButton>
  );
};

const Dispute = ({ returnSn, refreshList }: ReturnActionMercadoProps) => {
  const { t } = useTranslate();

  return (
    <StatusButton
      size="small"
      onClick={() => {
        return disputeReturn({
          platform: 'MERCADO',
          returnSn,
          operate: 'DISPUTE',
        })
          .then(() => {
            refreshList?.();
            message.success(t('common.feedback.saveSuccess'));
          })
          .catch(() => {
            message.error(t('common.feedback.saveFailedPleaseRetry'));
          });
      }}
    >
      {t('scene.orderWorkspace.returnRefundTab.action.requestPlatformIntervention')}
    </StatusButton>
  );
};

const ReturnReviewResolve = (props: ReturnActionMercadoProps) => {
  const { returnSn, refreshList } = props;
  const { t } = useTranslate();

  return (
    <StatusButton
      size="small"
      popConfirmProps={{
        title: t('scene.orderWorkspace.returnRefundTab.dialog.confirmInspectionPass'),
      }}
      onClick={() =>
        returnReviewResolve('MERCADO', returnSn)
          .then(() => {
            refreshList?.();
            message.success(t('common.feedback.saveSuccess'));
          })
          .catch(() => {
            message.error(t('common.feedback.saveFailedPleaseRetry'));
          })
      }
    >
      {t('scene.orderWorkspace.returnRefundTab.status.inspectionPassed')}
    </StatusButton>
  );
};

export const getReturnActionMercado = (props: ReturnActionMercadoProps) => {
  const { returnSn, refundAmount, unit, operate = [], refreshList } = props;

  return [
    operate.includes('CONFIRM_RETURN_REFUND') && <ReturnRefund {...props} />,
    operate.includes('OFFER') && (
      <MercadoOffer
        returnExternalId={returnSn}
        refundAmount={refundAmount}
        unit={unit}
        onSuccess={refreshList}
      />
    ),
    operate.includes('RETURN_REVIEW_OK') && <ReturnReviewResolve {...props} />,
    operate.includes('RETURN_REVIEW_FAIL') && <ReturnReviewReject {...props} />,
    operate.includes('CONFIRM_ONLY_REFUND') && <OnlyRefund {...props} />,
    operate.includes('DISPUTE') && <Dispute {...props} />,
  ];
};

export const ReturnActionMercado = (props: ReturnActionMercadoProps) => (
  <>{getReturnActionMercado(props)}</>
);
